import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'

interface FAQItem {
  question: string
  answer: string
}

const faqCategories = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is a 1031 exchange?',
        answer: 'A 1031 exchange (also called a like-kind exchange) is a tax-deferred exchange that allows you to sell an investment property and purchase a replacement property without paying capital gains taxes on the sale, as long as you follow specific IRS rules and timelines. Named after Section 1031 of the Internal Revenue Code, this strategy allows investors to defer capital gains taxes and potentially build wealth faster.'
      },
      {
        question: 'How do I get started with Open1031?',
        answer: 'Getting started is easy! Simply create an account, and our AI assistant Neela will guide you through the process. You can start by providing basic information about your exchange, and we\'ll handle the rest. Neela can help you create an account conversationally, or you can use our Exchange Hub to fill out forms step-by-step.'
      },
      {
        question: 'What information do I need to begin?',
        answer: 'To start your 1031 exchange, you\'ll need basic information about yourself (name, address, tax ID), details about the property you\'re selling (relinquished property address or legal description), buyer information, closing company details, and banking information if applicable. Our platform will guide you through collecting all necessary information step by step.'
      },
      {
        question: 'Do I need to be a real estate professional to use Open1031?',
        answer: 'Not at all! Open1031 is designed for investors of all experience levels. Our AI assistant Neela and intuitive interface make the process accessible to anyone. Whether you\'re a first-time exchanger or have completed multiple exchanges, our platform simplifies the process.'
      },
      {
        question: 'Can I start an exchange before I\'ve sold my property?',
        answer: 'Yes! You can begin setting up your exchange account and gathering information at any time. However, the official exchange period begins when you close on your relinquished property. Starting early allows you to be fully prepared when that deadline arrives.'
      }
    ]
  },
  {
    title: 'Exchange Process',
    items: [
      {
        question: 'What are the key deadlines for a 1031 exchange?',
        answer: 'There are two critical deadlines: (1) 45-day identification period - you must identify potential replacement properties within 45 days of closing on your relinquished property, and (2) 180-day exchange period - you must close on your replacement property within 180 days of the relinquished property closing. These deadlines are strict and cannot be extended, even for weekends or holidays.'
      },
      {
        question: 'What is the identification rule?',
        answer: 'You must follow one of three identification rules: (1) 3-Property Rule - identify up to 3 properties of any value, (2) 200% Rule - identify any number of properties as long as total value doesn\'t exceed 200% of relinquished property value, or (3) 95% Rule - identify any number and value, but must close on 95% of total identified value. Our platform helps you select and track which rule you\'re using.'
      },
      {
        question: 'Can I use the proceeds from my sale immediately?',
        answer: 'No. In a 1031 exchange, the proceeds must be held by a Qualified Intermediary (QI) until you close on your replacement property. You cannot have "constructive receipt" of the funds, or the exchange will be disqualified. Open1031 provides QI services and ensures your funds are held securely in compliance with IRS regulations.'
      },
      {
        question: 'What happens if I miss the 45-day identification deadline?',
        answer: 'Missing the 45-day deadline disqualifies your exchange, and you\'ll be required to pay capital gains taxes on the sale. This is why it\'s crucial to work with a platform like Open1031 that helps you track deadlines and sends reminders. Our system alerts you well in advance of critical dates.'
      },
      {
        question: 'Can I identify multiple replacement properties?',
        answer: 'Yes, depending on which identification rule you choose. Under the 3-Property Rule, you can identify up to 3 properties. Under the 200% Rule, you can identify any number as long as the total value doesn\'t exceed 200% of your relinquished property value. Under the 95% Rule, you can identify any number and value, but must close on at least 95% of the total identified value.'
      },
      {
        question: 'What types of properties qualify for a 1031 exchange?',
        answer: 'To qualify, both the relinquished and replacement properties must be held for investment or business use. This includes rental properties, commercial real estate, vacant land held for investment, and certain types of business property. Personal residences and properties held primarily for sale do not qualify.'
      }
    ]
  },
  {
    title: 'Platform & Features',
    items: [
      {
        question: 'What features does Open1031 offer?',
        answer: 'Open1031 provides a comprehensive platform including: AI-powered workflows with Neela assistant, document management and organization, capital gains calculator, property marketplace with qualified DSTs and direct properties, IRS form generation and filing, Qualified Intermediary services, deadline tracking and reminders, and compliance monitoring - all in one unified platform.'
      },
      {
        question: 'How secure is my information?',
        answer: 'Security is our top priority. We use bank-level encryption (AES-256), secure document storage, multi-factor authentication, and follow industry best practices for data protection including SOC 2 compliance. Your sensitive information, including tax IDs and financial data, is encrypted both in transit (TLS 1.3) and at rest. We never share your information with third parties without your explicit consent.'
      },
      {
        question: 'Can I access my exchange information on mobile?',
        answer: 'Yes! While our native mobile app is coming in Q2 2025, you can access the platform through any web browser on your mobile device. The interface is fully responsive and optimized for mobile use. You can upload documents, chat with Neela, track deadlines, and manage your exchange from anywhere.'
      },
      {
        question: 'How does the AI assistant Neela work?',
        answer: 'Neela is our AI-powered assistant that helps guide you through the entire 1031 exchange process. Neela can help you create an account, fill out forms, answer questions about 1031 exchanges, track deadlines, and provide personalized guidance. You can interact with Neela through text or voice input, and she\'s available 24/7.'
      },
      {
        question: 'Can I upload documents from my phone?',
        answer: 'Yes! You can upload documents directly from your mobile device\'s camera or photo library. Our platform accepts PDF, JPG, PNG, and other common document formats. Documents are automatically categorized and organized for easy access.'
      },
      {
        question: 'Does Open1031 integrate with other services?',
        answer: 'Yes, we integrate with various services including property data providers (Zillow, Redfin), Google Places for address autocomplete, and banking services. We\'re continuously adding new integrations to make your exchange process smoother. Check our documentation for the latest integrations.'
      }
    ]
  },
  {
    title: 'Pricing & Support',
    items: [
      {
        question: 'How much does Open1031 cost?',
        answer: 'Please contact our sales team for detailed pricing information. We offer flexible pricing plans based on your needs, including options for individual investors and larger portfolios. Pricing typically includes Qualified Intermediary services, document management, AI assistance, and platform access. Some features may be available on a per-use basis.'
      },
      {
        question: 'What kind of support do you provide?',
        answer: 'We provide comprehensive support including: 24/7 AI assistant (Neela), email support with response within 24 hours, phone support during business hours (Mon-Fri 9am-5pm PST), access to our knowledge base and documentation, video tutorials and webinars, and premium plans include dedicated account management and priority support.'
      },
      {
        question: 'Do you provide Qualified Intermediary services?',
        answer: 'Yes! Open1031 provides Qualified Intermediary (QI) services as part of our platform. We handle the escrow of your exchange proceeds, ensure compliance with all IRS requirements throughout the exchange process, and provide all necessary documentation. Our QI services are fully insured and compliant with all regulatory requirements.'
      },
      {
        question: 'Are there any hidden fees?',
        answer: 'No hidden fees. All costs are clearly disclosed upfront. Our pricing is transparent, and you\'ll know exactly what you\'re paying for before you commit. Some optional services like expedited processing or additional document storage may have additional fees, but these are always clearly communicated.'
      },
      {
        question: 'Do you offer refunds if I don\'t complete my exchange?',
        answer: 'Refund policies vary by service type. For platform subscriptions, we offer prorated refunds for unused time. For Qualified Intermediary services, fees are typically non-refundable once the exchange process has begun, as we\'ve already committed resources. Please contact support for specific refund inquiries.'
      },
      {
        question: 'Can I get help with tax preparation?',
        answer: 'While Open1031 provides all necessary documentation and forms for your 1031 exchange, we recommend consulting with a qualified tax professional for your overall tax strategy. We can generate IRS Form 8824 and other required documents, but final tax filing should be done with your CPA or tax advisor.'
      }
    ]
  },
  {
    title: 'Technical & Account',
    items: [
      {
        question: 'What browsers are supported?',
        answer: 'Open1031 works best on modern browsers including Chrome, Firefox, Safari, and Edge (latest versions). We recommend keeping your browser updated for the best experience and security. Mobile browsers are fully supported for responsive access.'
      },
      {
        question: 'Can multiple people access the same exchange?',
        answer: 'Yes! Open1031 supports team collaboration. You can invite team members, accountants, attorneys, and other advisors to your exchange with appropriate permission levels. Each user can have different access levels (read-only, write, admin) to ensure security while enabling collaboration.'
      },
      {
        question: 'How do I reset my password?',
        answer: 'You can reset your password from the login page by clicking "Forgot Password" and entering your email address. You\'ll receive a password reset link via email. If you have trouble, contact our support team for assistance.'
      },
      {
        question: 'Can I export my exchange data?',
        answer: 'Yes, you can export your exchange data at any time. This includes all forms, documents, property information, and transaction history. Data is exported in standard formats (PDF, CSV) for easy integration with other systems or for your records.'
      },
      {
        question: 'What happens to my data if I cancel my account?',
        answer: 'Your data is retained according to our data retention policy and legal requirements. You can request deletion of your data, but some information may be retained for legal and compliance purposes. Contact support for specific data deletion requests.'
      }
    ]
  }
]

export const FAQPage: React.FC = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#69adba]/10 dark:bg-[#69adba]/20 mb-6">
              <HelpCircle className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Frequently Asked Questions
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Find answers to common questions about 1031 exchanges and the Open1031 platform.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">
                  {category.title}
                </h2>
                <div className="space-y-4">
                  {category.items.map((item, itemIndex) => {
                    const key = `${categoryIndex}-${itemIndex}`
                    const isOpen = openItems[key]
                    return (
                      <div
                        key={itemIndex}
                        className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(categoryIndex, itemIndex)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                        >
                          <span className="font-semibold text-slate-900 dark:text-slate-100 pr-4">
                            {item.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-[#69adba] flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-[#69adba]/10 dark:bg-[#69adba]/20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              Still have questions?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Our team is here to help. Get in touch and we'll respond as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#69adba] hover:bg-[#5a9aa6]">
                  Contact Us
                </Button>
              </Link>
              <Button 
                variant="outline"
                onClick={() => {
                  // Open chat - will be handled by global chat launcher
                  window.dispatchEvent(new CustomEvent('openChat'))
                }}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

